import Markdown from '@/components/markdown/markdown';
import { OptimizedImage } from '@/components/optimized-image';
import { useEnterKeyGlobal } from '@/hooks/use-enter-key';
import { Box, Flex, Text } from '@cmpsr/components';
import { FC } from 'react';
import { useInfoImageInterstitialController } from './InfoImageInterstitial.controller';
import { InfoImageInterstitialProps } from './types';

// Aspect ratios for known images (width/height)
const imageAspectRatios: Record<string, number> = {
  'age': 400 / 325,
  'attitude': 400 / 348,
  'genetics': 400 / 276,
  'incidence': 400 / 621,
  'risk-factors': 400 / 316,
  'sex': 400 / 306,
  'survival-rate': 400 / 259,
};

export const InfoImageInterstitial: FC<InfoImageInterstitialProps> = ({ data, saveAnswer, goToNextSurveyItem }) => {
  const { title, subtitle, text, imageId, handleNextClick } = useInfoImageInterstitialController({
    saveAnswer,
    data,
    goToNextSurveyItem,
  });

  // Normalize imageId to handle spaces in filenames
  const normalizedImageId = imageId.replace(/\s+/g, '-');
  
  // Get aspect ratio for this image, default to a reasonable value if not found
  const aspectRatio = imageAspectRatios[normalizedImageId] || 1.2;

  // Define available sizes and their srcset descriptors
  const imageSizes = [
    { width: 300, descriptor: '300w' },
    { width: 400, descriptor: '400w' },
    { width: 600, descriptor: '600w' },
    { width: 600, descriptor: '600w', suffix: '@2x' },  // Actually 600px wide
    { width: 800, descriptor: '800w', suffix: '@2x' },  // Actually 800px wide  
    { width: 1200, descriptor: '1200w', suffix: '@2x' }, // Actually 1200px wide
  ];

  // Generate srcSet for a given format
  const generateSrcSet = (format: string) => {
    return imageSizes
      .map(({ width, descriptor, suffix = '' }) => {
        // For @2x images, use the base width in filename but actual width in descriptor
        const filenameWidth = suffix === '@2x' ? width / 2 : width;
        return `/images/info-interstitials/optimized/${normalizedImageId}-${filenameWidth}w${suffix}.${format} ${descriptor}`;
      })
      .join(',\n              ');
  };

  useEnterKeyGlobal(handleNextClick);

  return (
    <Box height="100%" width="100%" overflowY="auto" py="6">
      <Flex
        textAlign="center"
        gap={{ base: '6', lg: '24' }}
        alignItems="center"
        justifyContent="center"
        flexDirection={{ base: 'column', lg: 'row' }}
        id="info-image-interstitial"
        w="100%"
        minHeight="calc(100% - 48px)"
      >
        <Flex
          flexDirection="column"
          alignItems={{ base: 'center', md: 'center' }}
          justifyContent={{ base: 'flex-start', md: 'space-between' }}
          gap="4"
          width={{ base: '100%', md: 'auto' }}
          maxWidth={{ base: '300px', md: '400px' }}
          minWidth={{ base: '300px', md: '400px' }}
        >
          {title && (
            <Text variant="text-body-meta-medium" textTransform="uppercase" textAlign="left">
              <Markdown
                components={{
                  p: ({ node, ...props }) => <Text variant="text-body-meta-medium" {...props} />,
                  em: ({ node, ...props }) => (
                    <Text as="span" variant="text-body-meta-medium" color="salmon" {...props} />
                  ),
                }}
              >
                {title}
              </Markdown>
            </Text>
          )}
          <OptimizedImage
            src={`/images/info-interstitials/optimized/${normalizedImageId}-400w.png`}
            srcSet={generateSrcSet('png')}
            sizes="(max-width: 768px) 300px, (max-width: 1024px) 400px, 600px"
            alt={title || `Info about ${subtitle || 'this topic'}`}
            priority={true}
            objectFit="contain"
            aspectRatio={aspectRatio}
            skeletonProps={{
              borderRadius: 'lg',
              bg: 'gray.200',
            }}
            sources={[
              {
                type: 'image/avif',
                srcSet: generateSrcSet('avif'),
              },
            ]}
          />
        </Flex>
        <Flex
          textAlign="left"
          maxW={{ base: '450px', md: '400px' }}
          flexDirection="column"
          alignItems="center"
          px={{ base: '12', md: '0' }}
        >
          {subtitle && (
            <Text variant="text-body-regular" color="text-link-secondary-default">
              {subtitle}
            </Text>
          )}
          <Box>
            <Markdown
              components={{
                p: ({ node, ...props }) => <Text variant="text-body-meta-regular" color="text-secondary" {...props} />,
                br: () => <Box py="spacer-1" />,
              }}
            >
              {text}
            </Markdown>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};
